import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  TrayArrowDown, ArrowRight, EnvelopeSimple, Phone, Buildings, CaretDown, FilePdf, FileImage, Trash,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { money, relativeTime, dateTime } from "../../lib/format";
import { StatusChip } from "../../components/StatusChip";
import { Button } from "../../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const statusMeta = (s) => ({
  new: { label: "New", color: "#10B981", bg: "rgba(16,185,129,0.16)" },
  reviewing: { label: "Reviewing", color: "#FBBF24", bg: "rgba(251,191,36,0.16)" },
  converted: { label: "Converted", color: "#818CF8", bg: "rgba(129,140,248,0.16)" },
  declined: { label: "Declined", color: "#F87171", bg: "rgba(248,113,113,0.14)" },
}[s] || { label: s, color: "#94A3B8", bg: "rgba(148,163,184,0.14)" });

export default function Applications() {
  const nav = useNavigate();
  const { isOwner } = useAuth();
  const [apps, setApps] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = () => { api.get("/applications").then((r) => setApps(r.data)).catch(() => setApps([])); };
  useEffect(load, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/applications/${id}`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const convert = async (appn) => {
    if (appn.deal_id) { nav(`/admin/deals/${appn.deal_id}`); return; }
    try {
      const { data } = await api.post(`/applications/${appn.id}/convert`);
      toast.success("Draft deal created — finish building the offer");
      nav(`/admin/deals/${data.deal_id}/edit`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const viewFile = async (appId, fileId) => {
    try {
      const res = await api.get(`/applications/${appId}/files/${fileId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not open file"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this application permanently?")) return;
    try { await api.delete(`/applications/${id}`); toast.success("Application deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-5 md:p-8 max-w-5xl mx-auto">
      <div className="overline text-primary">Applications</div>
      <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Incoming Applications</h1>
      <p className="text-muted-foreground text-sm mt-1">Merchants who applied through your landing page. Convert one into a deal to build and send an offer.</p>

      {apps === null ? (
        <div className="mt-8 space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-24 rounded-xl bg-card border border-border animate-pulse" />)}</div>
      ) : apps.length === 0 ? (
        <div className="mt-8 rounded-[var(--radius)] border border-dashed border-border bg-card/50 py-20 flex flex-col items-center text-center">
          <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
            <TrayArrowDown size={26} weight="duotone" className="text-primary" />
          </div>
          <h3 className="font-display text-xl font-semibold">No applications yet</h3>
          <p className="text-muted-foreground text-sm mt-1 max-w-sm">When someone applies via your public landing page, they'll appear here for review.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {apps.map((a) => {
            const meta = statusMeta(a.status);
            const open = expanded === a.id;
            return (
              <div key={a.id} className="rounded-xl border border-border bg-card card-inner-glow overflow-hidden" data-testid={`application-${a.id}`}>
                <div className="p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4">
                  <button onClick={() => setExpanded(open ? null : a.id)} className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-2.5">
                      <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center shrink-0"><Buildings size={18} weight="duotone" className="text-muted-foreground" /></div>
                      <div className="min-w-0">
                        <div className="font-display font-semibold text-sm truncate">{a.business_name}</div>
                        <div className="text-xs text-muted-foreground truncate">{a.owner_name} · {relativeTime(a.created_at)}</div>
                      </div>
                    </div>
                  </button>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right hidden sm:block">
                      <div className="num font-semibold">{a.requested_amount ? money(a.requested_amount) : "—"}</div>
                      <div className="text-[11px] text-muted-foreground">requested</div>
                    </div>
                    <StatusChip label={meta.label} color={meta.color} bg={meta.bg} />
                    <Button size="sm" onClick={() => convert(a)} data-testid={`convert-${a.id}`} className="font-semibold" disabled={a.status === "declined"}>
                      {a.deal_id ? "View Deal" : "Create Deal"} <ArrowRight size={15} className="ml-1" />
                    </Button>
                    {isOwner && (
                      <button onClick={() => remove(a.id)} data-testid={`delete-app-${a.id}`} className="text-muted-foreground hover:text-destructive transition-colors" title="Delete application"><Trash size={17} /></button>
                    )}
                    <button onClick={() => setExpanded(open ? null : a.id)} className="text-muted-foreground"><CaretDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} /></button>
                  </div>
                </div>
                {open && (
                  <div className="border-t border-border p-4 md:p-5 bg-secondary/20 space-y-5">
                    <div>
                      <div className="overline text-muted-foreground mb-2">Business</div>
                      <div className="grid md:grid-cols-2 gap-x-8 gap-y-3">
                        <Info label="Legal Name" value={a.business_name} />
                        <Info label="DBA" value={a.dba || "—"} />
                        <Info label="Entity" value={a.entity_type || "—"} />
                        <Info label="EIN" value={a.ein || "—"} />
                        <Info label="Industry" value={a.industry || "—"} />
                        <Info label="Years in Business" value={a.years_in_business || "—"} />
                        <Info label="Annual Revenue" value={a.annual_revenue ? money(a.annual_revenue) : "—"} />
                        <Info label="Requested Amount" value={a.requested_amount ? money(a.requested_amount) : "—"} />
                        <div className="md:col-span-2"><Info label="Business Address" value={a.business_address || "—"} /></div>
                      </div>
                    </div>

                    <div>
                      <div className="overline text-muted-foreground mb-2">Owner / Guarantor</div>
                      <div className="grid md:grid-cols-2 gap-x-8 gap-y-3">
                        <Info label="Name" value={a.owner_name} />
                        <Info label="Ownership %" value={a.owner_ownership_pct || "—"} />
                        <Info label="SSN" value={<span className="num">{a.owner_ssn_last4 || "—"}</span>} />
                        <Info label="Date of Birth" value={a.owner_dob || "—"} />
                        <Info label="Email" value={<a href={`mailto:${a.owner_email}`} className="text-primary inline-flex items-center gap-1"><EnvelopeSimple size={14} /> {a.owner_email}</a>} />
                        <Info label="Phone" value={a.owner_phone ? <span className="inline-flex items-center gap-1"><Phone size={14} /> {a.owner_phone}</span> : "—"} />
                        <div className="md:col-span-2"><Info label="Home Address" value={a.owner_home_address || "—"} /></div>
                      </div>
                    </div>

                    {a.owner2_name && (
                      <div>
                        <div className="overline text-muted-foreground mb-2">Second Owner / Guarantor</div>
                        <div className="grid md:grid-cols-2 gap-x-8 gap-y-3">
                          <Info label="Name" value={a.owner2_name} />
                          <Info label="Ownership %" value={a.owner2_ownership_pct || "—"} />
                          <Info label="SSN" value={<span className="num">{a.owner2_ssn_last4 || "—"}</span>} />
                          <Info label="Date of Birth" value={a.owner2_dob || "—"} />
                          <Info label="Email" value={a.owner2_email || "—"} />
                          <Info label="Phone" value={a.owner2_phone || "—"} />
                          <div className="md:col-span-2"><Info label="Home Address" value={a.owner2_home_address || "—"} /></div>
                        </div>
                      </div>
                    )}

                    <div>
                      <div className="overline text-muted-foreground mb-2">Documents Provided</div>
                      {(!a.documents || a.documents.every((d) => d.files.length === 0)) ? (
                        <p className="text-sm text-muted-foreground">No documents uploaded with this application.</p>
                      ) : (
                        <div className="space-y-2">
                          {a.documents.filter((d) => d.files.length).map((d) => (
                            <div key={d.doc_type}>
                              <div className="text-xs font-medium mb-1">{d.name}</div>
                              <div className="flex flex-wrap gap-2">
                                {d.files.map((file) => {
                                  const Icon = (file.content_type || "").includes("pdf") ? FilePdf : FileImage;
                                  return (
                                    <button key={file.id} onClick={() => viewFile(a.id, file.id)} data-testid={`app-file-${file.id}`} className="flex items-center gap-1.5 text-xs bg-background border border-border rounded-lg px-2.5 py-1.5 hover:border-primary/40 transition-colors">
                                      <Icon size={14} weight="duotone" className="text-primary" />
                                      <span className="truncate max-w-[160px]">{file.original_filename}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {a.notes && <Info label="Applicant Notes" value={a.notes} />}

                    <div className="flex items-center gap-2 pt-1">
                      <span className="overline text-muted-foreground mr-1">Status</span>
                      <Select value={a.status} onValueChange={(v) => setStatus(a.id, v)}>
                        <SelectTrigger className="w-40 bg-background border-border h-9" data-testid={`status-${a.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="reviewing">Reviewing</SelectItem>
                          <SelectItem value="declined">Declined</SelectItem>
                          {a.status === "converted" && <SelectItem value="converted">Converted</SelectItem>}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const Info = ({ label, value }) => (
  <div>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-sm mt-0.5">{value}</div>
  </div>
);
